import { supabase, isSupabaseConfigured } from "@/lib/supabase";
import type { BookingPayPage } from "@/types/database";

export type PayApiResult<T> =
  | { ok: true; data: T }
  | { ok: false; error: string; code?: string; unconfigured?: boolean };

export async function fetchPayPage(token: string): Promise<PayApiResult<BookingPayPage>> {
  if (!supabase || !isSupabaseConfigured) {
    return {
      ok: false,
      error: "Payments are not configured yet.",
      unconfigured: true,
    };
  }
  if (!token || token.length < 32) {
    return { ok: false, error: "Invalid payment link." };
  }

  const { data, error } = await supabase.rpc("get_booking_pay_page", {
    p_token: token,
  });

  if (error) return { ok: false, error: error.message };
  const row = Array.isArray(data) ? data[0] : data;
  if (!row) return { ok: false, error: "This payment link is invalid or expired." };
  return { ok: true, data: row as BookingPayPage };
}

export async function startCheckout(
  payToken: string
): Promise<PayApiResult<{ url: string; session_id: string; reused?: boolean }>> {
  if (!supabase || !isSupabaseConfigured) {
    return {
      ok: false,
      error: "Checkout is unavailable until Supabase and Stripe are configured.",
      unconfigured: true,
    };
  }

  const { data, error } = await supabase.functions.invoke("create-checkout-session", {
    body: { pay_token: payToken },
  });

  const payload = data as {
    url?: string;
    session_id?: string;
    reused?: boolean;
    error?: string;
    code?: string;
  } | null;

  if (payload?.url) {
    return {
      ok: true,
      data: {
        url: payload.url,
        session_id: payload.session_id || "",
        reused: payload.reused,
      },
    };
  }

  return {
    ok: false,
    error: payload?.error || error?.message || "Checkout did not return a URL.",
    code: payload?.code,
  };
}

export async function fetchGroupPayPage(
  token: string
): Promise<PayApiResult<import("@/types/database").GroupPayRow[]>> {
  if (!supabase || !isSupabaseConfigured) {
    return { ok: false, error: "Payments are not configured yet.", unconfigured: true };
  }
  if (!token || token.length < 32) return { ok: false, error: "Invalid payment link." };
  const { data, error } = await supabase.rpc("get_group_pay_page" as never, { p_token: token } as never);
  if (error) return { ok: false, error: error.message };
  const rows = (data ?? []) as import("@/types/database").GroupPayRow[];
  if (rows.length === 0) return { ok: false, error: "This payment link is invalid or expired." };
  return { ok: true, data: rows };
}

export async function startGroupCheckout(
  groupToken: string
): Promise<PayApiResult<{ url: string }>> {
  if (!supabase || !isSupabaseConfigured) {
    return { ok: false, error: "Checkout is unavailable.", unconfigured: true };
  }
  const { data, error } = await supabase.functions.invoke("create-checkout-session", {
    body: { group_token: groupToken },
  });
  const payload = data as { url?: string; error?: string; code?: string } | null;
  if (payload?.url) return { ok: true, data: { url: payload.url } };
  return { ok: false, error: payload?.error || error?.message || "Checkout did not return a URL.", code: payload?.code };
}

export async function fetchBookingGroupInfo(token: string): Promise<{ group_token: string; group_count: number } | null> {
  if (!supabase || !isSupabaseConfigured || token.length < 32) return null;
  const { data } = await supabase.rpc("get_booking_group_info" as never, { p_token: token } as never);
  const row = (Array.isArray(data) ? data[0] : data) as { group_token: string; group_count: number } | undefined;
  return row && row.group_count > 1 ? row : null;
}
